import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import MaterialTable from "../../../components/Table/MaterialTable";
import ContactForm from "./ContactForm";

let lastContactId = 0; // We'll track the last generated contact_id here

const HotelContacts = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [brands, setBrands] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const columns = [
    { title: "ID", field: "id" },
    { title: "Contact ID", field: "contact_id" },
    { title: "Hotel ID", field: "hotel_id" },
    { title: "Department", field: "department" },
    { title: "Name", field: "name" },
    { title: "Position", field: "position" },
    { title: "Email", field: "email" },
    { title: "Phone", field: "phone" },
    {
      title: "Primary Contact",
      field: "is_primary",
      render: (rowData) => (
        <Chip
          label={rowData.is_primary ? "Yes" : "No"}
          color={rowData.is_primary ? "success" : "default"}
          size="small"
        />
      ),
    },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/HotelContact");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const fetchBrands = async () => {
    try {
      const response = await fetch("https://api.esthell.com/HotelBrand");
      if (!response.ok) throw new Error("Failed to fetch brands");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setBrands(responseData.data);
      }
    } catch (err) {
      console.error("Error fetching brands:", err);
      setError(err.message);
    }
  };

  const generateContactId = () => {
    lastContactId += 1; // Increment the contact_id with each call
    return lastContactId;
  };

  useEffect(() => {
    fetchData();
    fetchBrands();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editData?.id) {
        const payload = {
          data: {
            ...formData,
            contact_id: parseInt(editData.contact_id),
            hotel_id: parseInt(formData.hotel_id),
          },
        };

        const response = await fetch(
          `https://api.esthell.com/HotelContact/${editData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update contact");
        }
        setNotification({
          open: true,
          message: "Contact updated successfully!",
          type: "success",
        });
      } else {
        // Generate contact_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "Cont",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate contact ID");
        }

        const numberingData = await numberingResponse.json();
        const nextContactId = parseInt(numberingData[0].last_gen_number) + 1;

        const payload = {
          data: {
            ...formData,
            contact_id: nextContactId,
            hotel_id: parseInt(formData.hotel_id),
          },
        };

        const response = await fetch(
          "https://api.esthell.com/create/HotelContact",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create contact");
        }
        setNotification({
          open: true,
          message: "Contact created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/HotelContact/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete contact");
      }

      fetchData();
      setNotification({
        open: true,
        message: "Contact deleted successfully!",
        type: "success",
      });
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Contacts
        </Typography>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <ContactForm
          handleClose={() => {
            setShowForm(false);
            setEditData(null);
          }}
          handleSubmit={handleFormSubmit}
          initialData={editData}
          brands={brands}
        />
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelContacts;
